/**
 * Module-level registry of citation metadata, keyed by the ID minted on the
 * server (see `stream-events.ts`'s `citationSalt`/`citationSeq`).
 *
 * The chat stream emits two parallel signals for every cited span:
 *
 *   - a `citation-meta` SSE event carrying the full `ClientCitation` payload
 *     and a stable id, which the SSE adapter writes into this registry; and
 *   - an inline `<cite id="<id>"/>` marker spliced into the streamed text, which
 *     a remark plugin (`remark-citation-pills.ts`) resolves at markdown
 *     render time by looking up the id here.
 *
 * Why a module-level Map (not React context):
 *   1. The remark plugin runs inside `react-markdown`'s rehype pipeline, where
 *      hooks aren't available — the produced React node has to look up the
 *      citation by id at render time.
 *   2. assistant-ui's `Text` component is shared across all messages in a
 *      thread; a per-message citation map would require a context provider
 *      around each rendered message, which isn't accessible from outside the
 *      assistant-ui tree.
 *   3. IDs are minted with a per-stream random salt, so concurrent streams
 *      never collide and we can keep one global map without growing a
 *      per-conversation index.
 *
 * The map is unbounded for the lifetime of the tab, which is fine: each entry
 * is a small object (~200 bytes), and a heavy chat session would still be far
 * below any memory pressure threshold. `clearCitationRegistry` is exposed for
 * tests / future hygiene but isn't called in production.
 */
import type { ClientCitation } from '@upright/common/chat/citation-types';

export interface RegisteredCitation {
  citation: ClientCitation;
  /**
   * 1-based running index assigned by the server in the order citations
   * arrived over the stream. Used as the visible number on non-Upright pills
   * (Upright-internal pills show "U" instead).
   */
  index: number;
}

const registry = new Map<string, RegisteredCitation>();

export function registerCitation(id: string, entry: RegisteredCitation): void {
  registry.set(id, entry);
}

export function getCitation(id: string): RegisteredCitation | undefined {
  return registry.get(id);
}

/**
 * Look up a citation by its server-minted 1-based running index. Used by the
 * sidenote renderer to compare a citation's source against the immediately
 * preceding citation, so a run of citations from the same source can be
 * collapsed (title shown once, follow-ups rendered as continuation entries).
 *
 * The registry is intentionally a small map (each chat session writes one
 * entry per cited span), so the linear scan is fine — and avoids growing a
 * second `Map<number, string>` index that would have to stay in sync.
 */
export function getCitationByIndex(index: number): RegisteredCitation | undefined {
  for (const entry of registry.values()) {
    if (entry.index === index) return entry;
  }
  return undefined;
}

/** Test-only helper. Production code should not rely on the registry being empty. */
export function clearCitationRegistry(): void {
  registry.clear();
}
